"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ImLocalizationService = void 0;
const im_culture_provider_1 = require("../cultures/providers/im-culture-provider");
const im_currency_formatter_1 = require("../features/im-currency-formatter");
const im_date_formatter_1 = require("../features/im-date-formatter");
const im_api_translator_1 = require("../features/im-api-translator");
const im_file_translator_1 = require("../features/im-file-translator");
class ImLocalizationService {
    constructor(config) {
        this._culture = im_culture_provider_1.CultureProvider.getCultureByLocaleCurrency(config.useLocale, config.currencyCode);
        this._currencyFormatter = new im_currency_formatter_1.ImCurrencyFormatter(this._culture.currencyFormat);
        this._dateFormatter = new im_date_formatter_1.ImDateFormatter(this._culture.locale);
        if (!!config.resourceLoader.useApi) {
            this._translator = new im_api_translator_1.ImApiTranslator(config.resourceLoader, this._culture.locale, config.fallbackLocale);
        }
        else {
            this._translator = new im_file_translator_1.ImFileTranslator(config.resourceLoader, this._culture.locale);
            if (config.fallbackLocale && config.fallbackLocale.toLowerCase() !== config.useLocale.toLowerCase()) {
                this._baseTranslator = new im_file_translator_1.ImFileTranslator(config.resourceLoader, config.fallbackLocale);
            }
        }
        if (config.preloadResources) {
            config.preloadResources.map((resource) => {
                this._translator.loadTranslations(resource);
            });
        }
        config.useLocale = this._culture.locale;
        this._config = config;
    }
    updateCulture(locale, currencyCode) {
        this._culture = im_culture_provider_1.CultureProvider.getCultureByLocaleCurrency(locale, currencyCode);
        if (this._config.useLocale !== this._culture.locale) {
            this._config.useLocale = this._culture.locale;
            if (this._config.resourceLoader.useApi === false)
                this._translator = new im_file_translator_1.ImFileTranslator(this._config.resourceLoader, this._culture.locale);
            else
                this._translator = new im_api_translator_1.ImApiTranslator(this._config.resourceLoader, this._culture.locale);
            if (this._config.preloadResources) {
                this._config.preloadResources.map((resource) => {
                    this._translator.loadTranslations(resource);
                });
            }
        }
    }
    updateCurrency(currencyCode) {
        this._culture = im_culture_provider_1.CultureProvider.getCultureByLocaleCurrency(this._config.useLocale, currencyCode);
        this._currencyFormatter = new im_currency_formatter_1.ImCurrencyFormatter(this._culture.currencyFormat);
    }
    formatCurrency(amount) {
        return this._currencyFormatter.format(amount);
    }
    formatCurrencyByCode(amount, currencyCode) {
        const culture = im_culture_provider_1.CultureProvider.getCultureByLocaleCurrency(this._culture.locale, currencyCode);
        return this._currencyFormatter.formatByCurrencyCode(amount, culture.currencyFormat);
    }
    formatToShortCurrency(amount) {
        if (this._culture == null) {
            throw new Error("Culture is not setup. Use SetCulture method to setup");
        }
        return this._currencyFormatter.formatToShort(amount, this._culture.locale);
    }
    formatToShortCurrencyByCode(amount, currencyCode) {
        if (this._culture == null) {
            throw new Error("Culture is not setup. Use SetCulture method to setup");
        }
        else if (currencyCode.trim() === '') {
            throw new Error("Currency code is missing");
        }
        const culture = im_culture_provider_1.CultureProvider.getCultureByLocaleCurrency(this._culture.locale, currencyCode);
        return this._currencyFormatter.formatToShortByCode(amount, this._culture.locale, culture.currencyFormat);
    }
    formatDate(date, options) {
        let dateFormat = this._culture.dateFormat.short;
        if (options === null || options === void 0 ? void 0 : options.dateFormat) {
            if (options.dateFormat === "short")
                dateFormat = this._culture.dateFormat.short;
            else if (options.dateFormat === "medium")
                dateFormat = this._culture.dateFormat.medium;
            else if (options.dateFormat === "long")
                dateFormat = this._culture.dateFormat.long;
            else
                dateFormat = options.dateFormat;
        }
        return this._dateFormatter.format(date, dateFormat);
    }
    translate(key, options) {
        const returnKeyIfNotFound = options ? options.returnKeyIfNotFound : true;
        return new Promise((resolve, reject) => {
            this._translator.translate(key, options === null || options === void 0 ? void 0 : options.interpolateParams, (!this._baseTranslator && returnKeyIfNotFound))
                .then((value) => {
                if (typeof key === 'string') {
                    if (value === undefined) {
                        if (this._baseTranslator) {
                            this._baseTranslator.translate(key, options === null || options === void 0 ? void 0 : options.interpolateParams, returnKeyIfNotFound)
                                .then((pending_value) => {
                                if (pending_value === undefined && returnKeyIfNotFound) {
                                    resolve(key);
                                }
                                else {
                                    resolve(pending_value);
                                }
                            })
                                .catch(err => {
                                reject();
                            });
                        }
                        else {
                            resolve(returnKeyIfNotFound ? key : value);
                        }
                    }
                    else {
                        resolve(value);
                    }
                }
                else {
                    const pendingKeys = [];
                    for (const k in value) {
                        if (value[k] === undefined) {
                            if (this._baseTranslator) {
                                pendingKeys.push(k);
                            }
                            if (returnKeyIfNotFound) {
                                value[k] = k;
                            }
                        }
                    }
                    if (pendingKeys.length > 0) {
                        this._baseTranslator.translate(pendingKeys, options === null || options === void 0 ? void 0 : options.interpolateParams, returnKeyIfNotFound)
                            .then((pending_value) => {
                            for (const k in pending_value) {
                                if (pending_value[k]) {
                                    value[k] = pending_value[k];
                                }
                            }
                            resolve(value);
                        })
                            .catch(err => {
                            reject();
                        });
                    }
                    resolve(value);
                }
            })
                .catch(err => {
                reject();
            });
        });
    }
    translateInstant(key, options) {
        const returnKeyIfNotFound = options ? options.returnKeyIfNotFound : true;
        const value = this._translator.translateInstant(key, options === null || options === void 0 ? void 0 : options.interpolateParams, (!this._baseTranslator && returnKeyIfNotFound));
        if (typeof key === 'string') {
            if (value === undefined) {
                if (this._baseTranslator) {
                    const pending_value = this._baseTranslator.translateInstant(key, options === null || options === void 0 ? void 0 : options.interpolateParams, returnKeyIfNotFound);
                    if (pending_value === undefined && returnKeyIfNotFound) {
                        return key;
                    }
                    else {
                        return pending_value;
                    }
                }
                else {
                    return returnKeyIfNotFound ? key : value;
                }
            }
            else {
                return value;
            }
        }
        else {
            const pendingKeys = [];
            for (const k in value) {
                if (value[k] === undefined) {
                    if (this._baseTranslator) {
                        pendingKeys.push(k);
                    }
                    if (returnKeyIfNotFound) {
                        value[k] = k;
                    }
                }
            }
            if (pendingKeys.length > 0) {
                const pending_value = this._baseTranslator.translate(pendingKeys, options === null || options === void 0 ? void 0 : options.interpolateParams, returnKeyIfNotFound);
                for (const k in pending_value) {
                    if (pending_value[k]) {
                        value[k] = pending_value[k];
                    }
                }
                return value;
            }
            return value;
        }
    }
    get CurrentCulture() {
        return this._culture;
    }
    get AppName() {
        return this._config.resourceLoader.appName;
    }
}
exports.ImLocalizationService = ImLocalizationService;
